import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WeddingChecklist = () => {
  const [checklistItems, setChecklistItems] = useState([
    { id: 1, task: 'Book wedding venue', completed: true, priority: 'high', category: 'Venue' },
    { id: 2, task: 'Hire wedding photographer', completed: true, priority: 'high', category: 'Photography' },
    { id: 3, task: 'Choose catering service', completed: false, priority: 'high', category: 'Catering' },
    { id: 4, task: 'Book mehendi artist', completed: false, priority: 'medium', category: 'Beauty' },
    { id: 5, task: 'Order wedding invitations', completed: false, priority: 'medium', category: 'Invitations' },
    { id: 6, task: 'Select wedding music/DJ', completed: false, priority: 'medium', category: 'Entertainment' },
    { id: 7, task: 'Book transportation', completed: false, priority: 'low', category: 'Transportation' },
    { id: 8, task: 'Finalize guest list', completed: true, priority: 'high', category: 'Planning' },
    { id: 9, task: 'Choose wedding favors', completed: false, priority: 'low', category: 'Favors' },
    { id: 10, task: 'Book honeymoon', completed: false, priority: 'medium', category: 'Honeymoon' }
  ]);

  const [newTask, setNewTask] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  const handleToggleTask = (taskId) => {
    setChecklistItems(prev => 
      prev.map(item => 
        item.id === taskId 
          ? { ...item, completed: !item.completed }
          : item
      )
    );
  };

  const handleAddTask = () => {
    if (newTask.trim()) {
      const newItem = {
        id: Date.now(),
        task: newTask.trim(),
        completed: false,
        priority: 'medium',
        category: 'Other'
      };
      setChecklistItems(prev => [...prev, newItem]);
      setNewTask('');
    }
  };

  const handleDeleteTask = (taskId) => {
    setChecklistItems(prev => prev.filter(item => item.id !== taskId));
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-500';
      case 'medium': return 'text-yellow-500';
      case 'low': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  const getFilteredItems = () => {
    switch (activeFilter) {
      case 'completed': return checklistItems.filter(item => item.completed);
      case 'pending': return checklistItems.filter(item => !item.completed);
      case 'high': return checklistItems.filter(item => item.priority === 'high');
      default: return checklistItems;
    }
  };

  const completedCount = checklistItems.filter(item => item.completed).length;
  const completionPercentage = Math.round((completedCount / checklistItems.length) * 100);

  return (
    <div className="bg-white rounded-xl shadow-soft p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 md:mb-6 gap-2">
        <div>
          <h3 className="font-heading font-semibold text-lg md:text-xl text-text-primary">
            Wedding Checklist
          </h3>
          <p className="text-text-secondary text-xs md:text-sm">
            {completedCount} of {checklistItems.length} tasks completed ({completionPercentage}%)
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="text-right">
            <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-primary to-primary-600 flex items-center justify-center">
              <span className="text-white font-bold text-sm md:text-lg">{completionPercentage}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4 md:mb-6">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-primary to-primary-600 h-2 rounded-full transition-all duration-500"
            style={{ width: `${completionPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* Add New Task */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            placeholder="Add a new task..."
            className="flex-1 px-3 md:px-4 py-2 border border-border rounded-lg focus:outline-none focus:border-primary text-sm md:text-base"
            onKeyPress={(e) => e.key === 'Enter' && handleAddTask()}
          />
          <Button
            variant="primary"
            size="sm"
            iconName="Plus"
            onClick={handleAddTask}
            className="w-full sm:w-auto"
          >
            Add
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all', label: 'All Tasks' },
            { key: 'pending', label: 'Pending' },
            { key: 'completed', label: 'Completed' },
            { key: 'high', label: 'High Priority' }
          ].map(filter => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key)}
              className={`px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium transition-all duration-200 ${
                activeFilter === filter.key
                  ? 'bg-primary text-white' :'bg-surface-200 text-text-secondary hover:bg-surface-300'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Checklist Items */}
      <div className="space-y-2 max-h-80 md:max-h-96 overflow-y-auto">
        {getFilteredItems().map((item) => (
          <div 
            key={item.id}
            className={`flex items-center space-x-2 md:space-x-3 p-2 md:p-3 rounded-lg border transition-all duration-200 ${
              item.completed 
                ? 'bg-green-50 border-green-200' :'bg-surface-50 border-border hover:bg-surface-100'
            }`}
          >
            <button
              onClick={() => handleToggleTask(item.id)}
              className={`flex-shrink-0 w-4 h-4 md:w-5 md:h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                item.completed
                  ? 'bg-green-500 border-green-500 text-white' :'border-gray-300 hover:border-primary'
              }`}
            >
              {item.completed && <Icon name="Check" size={12} className="md:w-3.5 md:h-3.5" />}
            </button>

            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 gap-1">
                <span className={`text-xs md:text-sm font-medium ${
                  item.completed ? 'line-through text-gray-500' : 'text-text-primary'
                }`}>
                  {item.task}
                </span>
                <div className="flex items-center space-x-2">
                  <span className={`text-xs px-2 py-1 rounded-full bg-white ${getPriorityColor(item.priority)}`}>
                    {item.priority}
                  </span>
                  <span className="text-xs text-text-muted">{item.category}</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => handleDeleteTask(item.id)}
              className="flex-shrink-0 text-red-500 hover:text-red-700 transition-colors duration-200 p-1"
            >
              <Icon name="Trash2" size={14} className="md:w-4 md:h-4" />
            </button>
          </div>
        ))}
      </div>

      {getFilteredItems().length === 0 && (
        <div className="text-center py-6 md:py-8">
          <Icon name="CheckCircle" size={48} className="text-text-muted mx-auto mb-4" />
          <p className="text-text-secondary text-sm md:text-base">No tasks found for this filter</p>
        </div>
      )}
    </div>
  );
};

export default WeddingChecklist;